import { useMemo } from 'react';
import {
  Button,
  Col,
  Row,
  Card,
  ButtonToolbar,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { v4 as uuidv4 } from 'uuid';

import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';

import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderError, renderOffline } from '../components/render_helpers';
import SubmitButtonSpinner from '../components/submit_button_spinner';
import Field from '../components/form/rff_field';
import InputField from '../components/form/input_field';
import ReactDateTimeField from '../components/form/react_date_time_field';
import { coerceInput, pickValues, handleSubmitError } from '../lib/utils';

import {
  consignmentFormPageQuery,
  consignmentCreate as consignmentCreateMutation,
  consignmentUpdate as consignmentUpdateMutation,
} from '../graphql/consignment_queries';
import * as updateFunctions from '../update_functions';
import { getConsignmentDefaultValues } from '../defaults';
import { consignmentFormValidator } from '../validators';
import { consignmentWhiteList } from '../white_lists';

const ConsignmentForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const [consignmentCreate] = useMutation(consignmentCreateMutation);
  const [consignmentUpdate] = useMutation(consignmentUpdateMutation);
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    networkStatus: pageNetworkStatus,
  } = useQuery(consignmentFormPageQuery, {
    variables: {
      hasConsignmentId: !!params.id,
      consignmentId: params.id || 0,
    },
    notifyOnNetworkStatusChange: true,
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const initialValues = useMemo(() => {
    if (pageData?.consignment) {
      const values = pickValues(pageData.consignment, consignmentWhiteList);
      return values;
    }
    return getConsignmentDefaultValues();
  }, [pageData]);

  const onCancel = () => {
    navigate('/consignments');
  };

  const onFormSubmit = async (data) => {
    const uuid = uuidv4();
    const submitData = cloneDeep(data);
    let mutation;
    let mutationMessageAction;
    const input = coerceInput(submitData);
    const mutationData = {
      variables: { input },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'ConsignmentType',
        recordId: params.id || uuid,
      },
    };
    if (params.id) {
      mutation = consignmentUpdate;
      mutationMessageAction = 'update';
      mutationData.variables.id = params.id;
      mutationData.context.mutationType = 'UPDATE';
      mutationData.update = updateFunctions.consignmentUpdate;
      mutationData.optimisticResponse = updateFunctions.optimistic(
        'consignmentUpdate',
        mutationData,
        {},
        ['file']
      );
    } else {
      mutation = consignmentCreate;
      mutationData.context.mutationType = 'CREATE';
      mutationMessageAction = 'create';
      mutationData.update = updateFunctions.consignmentCreate;
      mutationData.optimisticResponse = updateFunctions.optimistic(
        'consignmentCreate',
        mutationData,
        {},
        ['file']
      );
    }
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await mutation(mutationData);
        toastSuccess(`Consignment ${mutationMessageAction} succeeded`);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        navigate('/consignments');
      } catch (err) {
        const { errorMessage, submitErrors } = handleSubmitError(err);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        toastError(errorMessage);
        return submitErrors;
      }
    } else {
      mutation(mutationData);
      toastWarning(
        `Consignment ${mutationMessageAction} ok locally. Go online to make permanent`
      );
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
      navigate('/consignments');
    }
    return undefined;
  };

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">
                {params.id ? 'Edit Consignment' : 'New Consignment'}
              </h1>
            </div>
            <div className="float-end">
              <LinkContainer to="/consignments">
                <Button className="me-2" variant="primary">
                  All Consignments
                </Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <FinalForm
            initialValues={initialValues}
            onSubmit={(data) => onFormSubmit(data)}
            validate={consignmentFormValidator}
            mutators={{ setFieldTouched }}
          >
            {({ handleSubmit, pristine, submitting }) => (
              <form noValidate>
                <Card>
                  <Card.Body>
                    <Field
                      name="siteId"
                      component={InputField}
                      asElement="select"
                      selectOptions={get(pageData, 'siteList', [])}
                      defaultSelectOptionText="select site..."
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                    >
                      Site
                    </Field>
                    <Field
                      name="supplierId"
                      component={InputField}
                      asElement="select"
                      selectOptions={get(pageData, 'supplierList', [])}
                      defaultSelectOptionText="select supplier..."
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                    >
                      Supplier
                    </Field>
                    {/* <Field
                      name="containerIdentifier"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Container Identifier
                    </Field> */}
                    <Field
                      name="shipmentNumber"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={2}
                      size="lg"
                    >
                      Shipment Number
                    </Field>
                    <Field
                      name="containerReference"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Container Reference
                    </Field>
                    <Field
                      name="containerNumber"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Container Number
                    </Field>
                    <Field
                      name="containerSealNumber"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Container Seal Number
                    </Field>
                    <Field
                      size="lg"
                      name="estimatedDeliveryAt"
                      labelWidth={4}
                      inputWidth={4}
                      component={ReactDateTimeField}
                      helpText="ddd Do MMMM YYYY HH:mm"
                      dateFormat="ddd Do MMMM YYYY"
                      timeFormat="HH:mm"
                      closeOnSelect
                      // initialViewMode="time"
                    >
                      Estimated Delivery At
                    </Field>
                    <Form.Group as={Row}>
                      <Col sm={12}>
                        <ButtonToolbar style={{ justifyContent: 'flex-end' }}>
                          <ButtonGroup className="me-2">
                            <Button
                              variant="danger"
                              onClick={onCancel}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={pristine || submitting}
                              onClick={handleSubmit}
                            >
                              {submitting && <SubmitButtonSpinner />}
                              {params.id ? 'Update' : 'Create'}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </form>
            )}
          </FinalForm>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ConsignmentForm;
